import type { Session } from '@ory/kratos-client';

import type { GlobalViewer } from '@r-client/shared/data/auth';
import { E_JURISDICTION, E_REGION } from '@r-client/shared/feature/region';

export interface IJurisdictionAccounts {
  hasUsAccount: boolean;
  hasEuAccount: boolean;
  hasUkAccount: boolean;
}

/**
 * Extracts jurisdiction account presence from an Ory session's metadata.
 * Reads `has_us_account`, `has_eu_account`, `has_uk_account` from
 * `session.identity.metadata_public`.
 */
export const getJurisdictionAccountsFromSession = (
  session: Session | undefined
): IJurisdictionAccounts => {
  const metadata = session?.identity?.metadata_public;
  return {
    hasUsAccount: metadata?.has_us_account === true,
    hasEuAccount: metadata?.has_eu_account === true,
    hasUkAccount: metadata?.has_uk_account === true,
  };
};

/**
 * Extracts jurisdiction account presence from a global auth viewer's session metadata.
 * Reads `has_us_account`, `has_eu_account`, `has_uk_account` from
 * `viewer.info.identity.metadata_public` (available client-side via Ory's toSession()).
 */
export const getJurisdictionAccounts = (
  viewer: GlobalViewer | undefined
): IJurisdictionAccounts => {
  if (!viewer?.isAuthenticated) {
    return { hasUsAccount: false, hasEuAccount: false, hasUkAccount: false };
  }

  return getJurisdictionAccountsFromSession(viewer.info as Session | undefined);
};

/**
 * Returns whether the authenticated user has an account in the given region.
 * For `E_REGION.EUROPE`, both EU and UK accounts are treated as a match.
 */
export const hasAccountInRegion = (
  viewer: GlobalViewer | undefined,
  region: E_REGION
): boolean => {
  const accounts = getJurisdictionAccounts(viewer);
  if (region === E_REGION.US) return accounts.hasUsAccount;
  if (region === E_REGION.EUROPE)
    return accounts.hasEuAccount || accounts.hasUkAccount;
  return false;
};

/**
 * Given the user's jurisdiction accounts and the current jurisdiction cookie value,
 * returns the jurisdiction that should be set, or `undefined` if no change is needed.
 *
 * Rules:
 * - If the user has no EU or UK account, leave the cookie as-is (return undefined).
 * - If the current cookie already matches an account they have, keep it (return undefined).
 * - Otherwise, set the cookie to the jurisdiction they do have (EU preferred over GB).
 */
export const resolveJurisdictionCookie = (
  accounts: IJurisdictionAccounts,
  currentCookieValue: string | undefined
): E_JURISDICTION | undefined => {
  const { hasEuAccount, hasUkAccount } = accounts;

  // No EU/UK accounts — leave cookie as-is
  if (!hasEuAccount && !hasUkAccount) return undefined;

  // Current cookie already matches an account they have
  if (currentCookieValue === E_JURISDICTION.EU && hasEuAccount)
    return undefined;
  if (currentCookieValue === E_JURISDICTION.GB && hasUkAccount)
    return undefined;

  // Set to whichever jurisdiction they have (EU preferred)
  return hasEuAccount ? E_JURISDICTION.EU : E_JURISDICTION.GB;
};
